import http from '@monolith/legacy/services/api/http';
import { isAxiosError } from 'axios';
import { deserialize } from '@monolith/legacy/services/utils/jsonapi-parser';
import { Cart2, PurchasingLists, PurchasableAggregate } from '@monolith/legacy/modules/cart-2/types';
import { Amount } from '@core/types/amount';
import { isEnabled } from '@monolith/legacy/services/features';
import { Cart as CartClient } from '@ankorstore/api-internal/Cart';

import { jsonApiConfigs } from '@monolith/legacy/services/api/configs/jsonapi';
import { CartAddress } from '../types/cart-address';
import type { AnkorstoreInternalPurchasableAggregateResource, AnkorstoreInternalPurchasableItemResource } from '@ankorstore/api-internal/data-contracts';

interface PaymentSummary {
  isShippable: boolean;
  grandTotal: Amount;
  billingAddress: CartAddress;
  shippingAddress: CartAddress;
}

// Type-safe field lists derived from generated OpenAPI types.
// We list fields explicitly (runtime values needed for query strings),
// but TypeScript validates they match the OpenAPI-generated types.
type PurchasableAggregateField =
  | keyof NonNullable<AnkorstoreInternalPurchasableAggregateResource['attributes']>
  | keyof NonNullable<AnkorstoreInternalPurchasableAggregateResource['relationships']>;

type PurchasableItemField = keyof NonNullable<AnkorstoreInternalPurchasableItemResource['attributes']>;

const PURCHASABLE_AGGREGATE_FIELDS = [
  'totalAmountBeforeDiscount',
  'totalAmountAfterDiscount',
  'selected',
  'hasMultiDiscounts',
  'globalDiscountPercentage',
  'globalDiscountType',
  'itemCount',
  'hasReachedBrandMinimumOrderValue',
  'applicableFees',
  'canShipToRetailerCountry',
  'isPreparedOrder',
  'everyProductOutOfStock',
  'unavailableItemsCount',
  'hasFreshProducts',
  'hasInsufficientStockItems',
  'hasNeedsFreshInputProducts',
  'estimatedDelivery',
  'repeatOffer',
  'brand',
  'items',
] as const satisfies readonly PurchasableAggregateField[];

const PURCHASABLE_ITEM_FIELDS = [
  'name',
  'path',
  'options',
  'imagePath',
  'availability',
  'unitMultiplier',
  'availableAt',
  'pricing',
  'stock',
  'applicableFees',
  'storageTemperature',
  'needsFreshInput',
] as const satisfies readonly PurchasableItemField[];

export function buildSparseFieldsets(excludeFields: string[]): Record<string, string> {
  const aggregateFields = (PURCHASABLE_AGGREGATE_FIELDS as readonly string[]).filter((field) => !excludeFields.includes(field));
  const itemFields = (PURCHASABLE_ITEM_FIELDS as readonly string[]).filter((field) => !excludeFields.includes(field));

  return {
    'fields[purchasable-aggregates]': aggregateFields.join(','),
    'fields[purchasable-items]': itemFields.join(','),
  };
}

// Cart page can defer applicableFees to a lazy per-brand load; checkout (cart-summary) never does.
const deferApplicableFeesParam = (): Record<string, string> =>
  isEnabled('disable-franco-computation-on-cart') ? buildSparseFieldsets(['applicableFees']) : {};

const shouldDeferItems = (): boolean => !isEnabled('disable_RCN-262_lazy_load_cart_brand_items');

const getCartSparseFieldsetParams = (): Record<string, string> => {
  const excludeFields: string[] = [];
  if (isEnabled('disable-franco-computation-on-cart')) {
    excludeFields.push('applicableFees');
  }
  if (shouldDeferItems()) {
    excludeFields.push('items');
  }
  return excludeFields.length > 0 ? buildSparseFieldsets(excludeFields) : {};
};

export const getCart = (cartId: string): Promise<Cart2> =>
  http()
    .get(`/api/internal/v1/cart/carts/${cartId}`, {
      ...jsonApiConfigs,
      params: {
        include: shouldDeferItems()
          ? 'list.itemAggregates.brand'
          : 'list.itemAggregates.brand,list.itemAggregates.items.purchasableItem',
        ...getCartSparseFieldsetParams(),
      },
    })
    .then(({ data }) => deserialize(data))
    .then(({ data }) => data?.['list']?.data as Cart2);

export const getBrandCartExpand = async (brandUuid: string): Promise<PurchasableAggregate | null> => {
  try {
    const response = await new CartClient().getCartExpandBrand({
      brandUuid,
      include: 'brand,items,items.purchasableItem',
    });
    const deserialized = deserialize(response);
    return (deserialized.data as PurchasableAggregate) ?? null;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return null;
    }
    throw e;
  }
};

export const getCartSummary = (cartId: string, included: string[] = []) =>
  http()
    .get(`/api/internal/v1/cart/cart-summary/${cartId}`, {
      ...jsonApiConfigs,
      params: { include: included.join() },
    })
    .then(({ data }) => deserialize(data));

export const getPaymentSummary = async (cartId: string): Promise<PaymentSummary> =>
  (({
    data: {
      meta: { isShippable },
      summary: { grandTotal },
      billingAddress: { data: billingAddress },
      shippingAddress: { data: shippingAddress },
    },
  }) => ({ isShippable, grandTotal, billingAddress, shippingAddress }))(
    await getCartSummary(cartId, ['billingAddress', 'shippingAddress'])
  );

export const updateItemsQuantities = async (
  purchasingListId: string,
  items: Record<string, number>,
  mergedQuantity = false
): Promise<Cart2> => {
  const response = await http().patch(
    `/api/internal/v1/cart/purchasing-lists/${purchasingListId}/-actions/content-update`,
    {
      data: {
        attributes: {
          items: Object.entries(items).map(([itemId, quantity]) => ({ variantId: itemId, quantity })),
          mergeQuantities: mergedQuantity,
        },
      },
    },
    {
      params: {
        include: 'itemAggregates.brand,itemAggregates.items.purchasableItem',
        ...deferApplicableFeesParam(),
      },
      ...jsonApiConfigs,
    }
  );

  const formattedResponse = deserialize(response.data);
  return formattedResponse.data as Cart2;
};

export const updateBrandSelection = async (purchasingListId: string, aggregates: PurchasableAggregate[]): Promise<Cart2> => {
  const payload = aggregates.map((aggregate) => ({
    aggregateId: aggregate.id,
    selected: aggregate.selected,
  }));
  const response = await new CartClient().purchasingListActionSelectionUpdate(
    {
      purchasingList: purchasingListId,
      include: 'itemAggregates.brand,itemAggregates.items.purchasableItem',
      ...buildSparseFieldsets(isEnabled('disable-franco-computation-on-cart') ? ['applicableFees'] : []),
      // `include` isn't in the generated PurchasingListActionSelectionUpdateParams type, but the
      // backend requires it to return itemAggregates at all - fields[] alone isn't enough (RCN-264).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    {
      data: {
        attributes: {
          items: payload,
        },
      },
    }
  );
  const formattedResponse = deserialize(response);
  return formattedResponse.data as Cart2;
};

// No longer used (maybe in the future if the savedForLater is no more fetched in the cart)

// export const getCounts = async (retailerId): Promise<CartCount> => {
//   const response = await http().get(`api/internal/v1/cart/item-counter/${retailerId}`, {
//     ...jsonApiConfigs,
//   });
//   return deserialize(response.data?.data?.attributes);
// };

export const getSavedForLater = async (purchasingListId: string): Promise<Cart2> => {
  const response = await http().get(`api/internal/v1/cart/purchasing-lists/${purchasingListId}`, {
    ...jsonApiConfigs,
    params: {
      include: 'itemAggregates.brand,itemAggregates.items.purchasableItem',
      ...deferApplicableFeesParam(),
    },
  });

  const formattedResponse = deserialize(response.data);
  return formattedResponse?.data as Cart2;
};

export const moveBrandBetweenLists = async (params, items): Promise<PurchasingLists> => {
  const { destinationListId, sourceListId } = params;
  const response = await http().patch(
    'api/internal/v1/cart/purchasing-lists/-actions/cross-list-operation',
    {
      data: {
        attributes: {
          destinationListId,
          sourceListId,
          operation: 'move',
          items,
          mergeQuantities: false,
        },
      },
    },
    {
      ...jsonApiConfigs,
      params: {
        include:
          'sourceList.itemAggregates.brand,sourceList.itemAggregates.items.purchasableItem,destinationList.itemAggregates.brand,destinationList.itemAggregates.items.purchasableItem',
        ...deferApplicableFeesParam(),
      },
    }
  );
  const formattedResponse = deserialize(response.data);
  return formattedResponse?.data as PurchasingLists;
};
